/**
 * AdminDashboard.jsx — v10
 * =========================
 * Matches MIS PettyCashAdmin.jsx styling exactly:
 *  - Plain bordered table for folders (no cards grid)
 *  - Summary row as border-cells (no stat cards)
 *  - No hover highlights on data rows
 *  - No colored icons in data cells — plain text only
 *  - Excel-cell aesthetic, Segoe UI font
 *  - Fuel column preserved (shown when data has fuel records)
 *  - Procurement ATX tab preserved
 *  - File preview modal preserved
 *  - FolderDataTable used for invoice data tab
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Layout, Typography, Button, Menu, message, Tag, Avatar, Dropdown,
  Table, Spin, Empty, Input, Badge, Modal, Tabs, Space, Popconfirm,
  Checkbox, Tooltip,
} from 'antd';
import {
  LogoutOutlined, FolderOutlined, FolderOpenOutlined, SettingOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined, HomeOutlined, ShoppingCartOutlined,
  BankOutlined, ProfileOutlined, FileTextOutlined, FilePdfOutlined,
  FileImageOutlined, FileOutlined, DownloadOutlined,
  SearchOutlined, ReloadOutlined, AppstoreOutlined,
  ArrowLeftOutlined, DeleteOutlined, EyeOutlined,
  ExportOutlined, CloseOutlined, CheckSquareOutlined,
  AuditOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import ProcurementAccountsView from './ProcurementAccountsView';
import FolderDataTable from './FolderDataTable';

const { Header, Content, Sider } = Layout;
const { Text } = Typography;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const api = axios.create({ baseURL: API_BASE_URL });

/* ── Helpers ── */
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
};

const getFileIcon = (filename, size = 16) => {
  if (!filename) return <FileTextOutlined style={{ fontSize: size, color: '#888' }} />;
  const ext = typeof filename === 'string' ? filename.split('.').pop().toLowerCase() : '';
  if (ext === 'pdf') return <FilePdfOutlined style={{ color: '#c41a1a', fontSize: size }} />;
  if (['xlsx', 'xls', 'csv'].includes(ext)) return <FileOutlined style={{ color: '#1a7a1a', fontSize: size }} />;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <FileImageOutlined style={{ color: '#b38600', fontSize: size }} />;
  return <FileTextOutlined style={{ color: '#888', fontSize: size }} />;
};

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

/* ── Inline styles matching MIS exactly ── */
const S = {
  page:         { fontFamily: "'Segoe UI', Calibri, 'Helvetica Neue', sans-serif", fontSize: 13 },
  pageTitle:    { fontSize: 15, fontWeight: 700, color: '#1a1a1a', letterSpacing: '-0.2px' },
  pageSubtitle: { fontSize: 11.5, color: '#777', marginTop: 1 },
  summaryRow:   { display: 'flex', gap: 0, borderBottom: '1px solid #d9d9d9', borderTop: '1px solid #d9d9d9', marginBottom: 16, background: '#fff' },
  summaryCell:  (isLast) => ({ flex: '1 1 0', padding: '10px 16px', borderRight: isLast ? 'none' : '1px solid #e8e8e8' }),
  summaryLabel: { fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 1 },
  summaryValue: (color) => ({ fontSize: 18, fontWeight: 700, color: color || '#1a1a1a', lineHeight: '1.3' }),
  cellText:     { fontSize: 12, color: '#1a1a1a' },
  cellBold:     { fontSize: 12, fontWeight: 600, color: '#1a1a1a' },
  cellMuted:    { fontSize: 11, color: '#999' },
  toolbar:      { display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' },
  link:         { color: '#0958d9', cursor: 'pointer', fontWeight: 600, fontSize: 12, background: 'none', border: 'none', padding: 0 },
  plainBtn:     { borderRadius: 3, fontSize: 11 },
  moduleBtn:    (active) => ({ borderRadius: 3, fontSize: 12, fontWeight: active ? 600 : 400, color: active ? '#fff' : '#333', background: active ? '#1a5276' : '#fff', borderColor: active ? '#1a5276' : '#d9d9d9' }),
};

const AdminDashboard = ({ username, role, onLogout }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedModule, setSelectedModule] = useState('all');
  const [loading, setLoading] = useState(false);
  const [documentsData, setDocumentsData] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [folderTab, setFolderTab] = useState('data');

  // File operations
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  // Preview
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewFilename, setPreviewFilename] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const isProcurementATX = selectedModule === 'procurement-atx';
  const displayName = username.split('@')[0] || username;
  const initials = displayName.substring(0, 2).toUpperCase();

  /* ── Data fetching ── */
  const fetchDocuments = useCallback(async (module = 'all') => {
    if (module === 'procurement-atx') return;
    setLoading(true);
    try {
      const params = module !== 'all' ? { module } : {};
      const response = await api.get('/api/admin/documents', { params });
      if (response.data.success) setDocumentsData(response.data.data);
      else message.error('Failed to load documents');
    } catch (err) {
      message.error(err.code === 'ERR_NETWORK' ? 'Cannot connect to server' : 'Failed to load documents');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (!isProcurementATX) fetchDocuments(selectedModule); }, [selectedModule, fetchDocuments, isProcurementATX]);

  /* ── File actions ── */
  const handleDownload = async (fileKey, filename) => {
    setDownloadingKey(fileKey);
    try {
      const r = await api.get('/api/admin/download-url', { params: { key: fileKey } });
      if (r.data.success && r.data.url) { const a = document.createElement('a'); a.href = r.data.url; a.download = filename; a.target = '_blank'; document.body.appendChild(a); a.click(); document.body.removeChild(a); }
      else message.error('Failed to get download link');
    } catch { message.error('Download failed'); }
    finally { setDownloadingKey(null); }
  };

  const handlePreview = async (fileKey, filename) => {
    setPreviewLoading(true); setPreviewFilename(filename); setPreviewVisible(true);
    try {
      const r = await api.get('/api/admin/preview-url', { params: { key: fileKey } });
      if (r.data.success && r.data.url) setPreviewUrl(r.data.url);
      else { message.error('Preview failed'); setPreviewVisible(false); }
    } catch { message.error('Preview failed'); setPreviewVisible(false); }
    finally { setPreviewLoading(false); }
  };

  const handleDeleteFile = async (fileKey, filename) => {
    try {
      const r = await api.delete('/api/admin/delete-file', { params: { key: fileKey } });
      if (r.data.success) { message.success(`Deleted ${filename}`); fetchDocuments(selectedModule); }
      else message.error('Delete failed');
    } catch { message.error('Delete failed'); }
  };

  const handleBulkDelete = async () => {
    if (selectedKeys.size === 0) return;
    setDeleting(true);
    try {
      const keys = Array.from(selectedKeys);
      const r = await api.post('/api/admin/delete-files', { keys });
      if (r.data.deleted > 0) { message.success(`Deleted ${r.data.deleted} of ${r.data.total} files`); setSelectedKeys(new Set()); setSelectionMode(false); fetchDocuments(selectedModule); }
      else message.error(r.data.message || 'Bulk delete failed');
    } catch { message.error('Bulk delete failed'); }
    finally { setDeleting(false); }
  };

  const toggleFileSelection = (key) => { setSelectedKeys(prev => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; }); };
  const selectAllInFolder = (files) => { const keys = files.map(f => f.key); setSelectedKeys(prev => { const next = new Set(prev); const allSelected = keys.every(k => next.has(k)); keys.forEach(k => allSelected ? next.delete(k) : next.add(k)); return next; }); };

  /* ── Menu ── */
  const handleMenuClick = ({ key }) => {
    setSelectedModule(key); setSelectedFolder(null); setSearchText('');
    setSelectionMode(false); setSelectedKeys(new Set()); setFolderTab('data');
  };

  /* ── Derived data ── */
  const summaryData = documentsData?.summary || { total_files: 0, total_size: 0, modules: {} };
  const siteMod = summaryData.modules?.['site-petty-cash'] || {};
  const procMod = summaryData.modules?.['procurement-pc'] || {};

  const getAllFoldersList = () => {
    const all = [];
    for (const modKey of Object.keys(documentsData?.modules || {})) {
      const label = modKey === 'site-petty-cash' ? 'Site PC' : 'Procurement';
      for (const f of Object.values(documentsData.modules[modKey].folders)) {
        if (f.file_count > 0) all.push({ ...f, module: modKey, moduleLabel: label });
      }
    }
    return all.sort((a, b) => b.file_count - a.file_count);
  };

  const getFoldersList = (moduleKey) => {
    if (!documentsData?.modules?.[moduleKey]) return [];
    return Object.values(documentsData.modules[moduleKey].folders)
      .sort((a, b) => parseInt(a.folder_number) - parseInt(b.folder_number));
  };

  const foldersList = selectedModule === 'all'
    ? getAllFoldersList()
    : getFoldersList(selectedModule).map(f => ({
        ...f, module: selectedModule,
        moduleLabel: selectedModule === 'site-petty-cash' ? 'Site PC' : 'Procurement',
      }));

  const getFilteredFiles = (files) => {
    if (!searchText.trim()) return files;
    const term = searchText.toLowerCase();
    return files.filter(f => f.filename.toLowerCase().includes(term));
  };

  /* ── File columns ── */
  const fileColumns = useMemo(() => {
    const cols = [];
    if (selectionMode) { cols.push({ title: '', key: 'select', width: 40, render: (_, r) => <Checkbox checked={selectedKeys.has(r.key)} onChange={() => toggleFileSelection(r.key)} /> }); }
    cols.push(
      {
        title: 'File', dataIndex: 'filename', key: 'filename',
        render: (text) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {getFileIcon(text, 13)}
            <Tooltip title={text}><span style={{ fontSize: 12, color: '#1a1a1a', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{text}</span></Tooltip>
          </div>
        ),
      },
      { title: 'Size', dataIndex: 'size', key: 'size', width: 90, render: (s) => <span style={S.cellMuted}>{formatFileSize(s)}</span>, sorter: (a, b) => a.size - b.size },
      { title: 'Modified', dataIndex: 'last_modified', key: 'mod', width: 150, render: (v) => <span style={{ fontSize: 11, color: '#999' }}>{formatDate(v)}</span>, sorter: (a, b) => new Date(a.last_modified) - new Date(b.last_modified), defaultSortOrder: 'descend' },
      {
        title: '', key: 'actions', width: 120, align: 'center',
        render: (_, r) => {
          const ext = r.filename?.split('.').pop().toLowerCase();
          const canPreview = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
          return (
            <Space size={4}>
              {canPreview && <button style={{ ...S.link, color: '#1a5276' }} onClick={() => handlePreview(r.key, r.filename)}><EyeOutlined style={{ fontSize: 12 }} /></button>}
              <button style={{ ...S.link, color: '#0958d9' }} onClick={() => handleDownload(r.key, r.filename)}>{downloadingKey === r.key ? <Spin size="small" /> : <DownloadOutlined style={{ fontSize: 12 }} />}</button>
              <Popconfirm title="Delete this file?" onConfirm={() => handleDeleteFile(r.key, r.filename)} okText="Delete" okButtonProps={{ danger: true, size: 'small' }}>
                <button style={{ ...S.link, color: '#c41a1a' }}><DeleteOutlined style={{ fontSize: 12 }} /></button>
              </Popconfirm>
            </Space>
          );
        },
      },
    );
    return cols;
  }, [selectionMode, selectedKeys, downloadingKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Profile menu ── */
  const profileMenuItems = [
    { key: 'header', label: (<div style={{ padding: '4px 0' }}><Text strong style={{ display: 'block', fontSize: 13 }}>{displayName}</Text><Text type="secondary" style={{ fontSize: 11 }}>{role?.replace('_', ' ').toUpperCase() || 'ADMIN'}</Text></div>), disabled: true },
    { type: 'divider' },
    { key: 'profile', icon: <ProfileOutlined />, label: 'Profile' },
    { key: 'settings', icon: <SettingOutlined />, label: 'Settings' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', danger: true },
  ];
  const handleProfileMenu = ({ key }) => { if (key === 'logout') onLogout(); };

  /* ── Sidebar items ── */
  const sidebarItems = [
    { key: 'pc-group', type: 'group', label: !collapsed ? <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: '#888' }}>PETTY CASH</span> : null,
      children: [
        { key: 'all', icon: <AppstoreOutlined style={{ fontSize: 15 }} />, label: 'All Documents' },
        { key: 'site-petty-cash', icon: <BankOutlined style={{ fontSize: 15 }} />, label: 'Site Petty Cash' },
        { key: 'procurement-pc', icon: <ShoppingCartOutlined style={{ fontSize: 15 }} />, label: 'Procurement PC' },
      ],
    },
    { key: 'proc-group', type: 'group', label: !collapsed ? <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: '#888', marginTop: 12, display: 'inline-block' }}>PROCUREMENT</span> : null,
      children: [
        { key: 'procurement-atx', icon: <AuditOutlined style={{ fontSize: 15 }} />, label: 'Procurement ATX' },
      ],
    },
  ];

  /* ═══════════════════════════════════════════
     RENDER: Folder Detail
     ═══════════════════════════════════════════ */
  const renderFolderDetail = () => {
    const { module: modKey, folderNum } = selectedFolder;
    const folderData = documentsData?.modules?.[modKey]?.folders?.[folderNum];
    if (!folderData) return <Empty description="Folder not found" />;
    const files = getFilteredFiles(folderData.files || []);
    const modLabel = modKey === 'site-petty-cash' ? 'Site Petty Cash' : 'Procurement PC';

    return (
      <div>
        {/* Folder header */}
        <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Button icon={<ArrowLeftOutlined />} size="small" style={S.plainBtn}
            onClick={() => { setSelectedFolder(null); setSearchText(''); setSelectionMode(false); setSelectedKeys(new Set()); setFolderTab('data'); }}>Back</Button>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>Folder {folderNum}</span>
          <span style={{ fontSize: 12, color: '#888' }}>{modLabel}</span>
          <span style={{ fontSize: 12, color: '#888' }}>{folderData.file_count} files</span>
          <span style={{ fontSize: 12, color: '#888' }}>{formatFileSize(folderData.total_size)}</span>
        </div>

        {/* Tabs: Invoice Data (default) + Files */}
        <Tabs activeKey={folderTab} onChange={k => { setFolderTab(k); setSelectionMode(false); setSelectedKeys(new Set()); }} items={[
          {
            key: 'data',
            label: <span>Invoice Data</span>,
            children: (
              <FolderDataTable
                folderNumber={folderNum}
                moduleType={modKey}
                folderName={folderNum}
                username={username}
              />
            ),
          },
          {
            key: 'files',
            label: <span>Files <Badge count={folderData.file_count} style={{ backgroundColor: '#1a5276', marginLeft: 6 }} size="small" /></span>,
            children: (
              <div>
                <div style={S.toolbar}>
                  <Input placeholder="Search files..." prefix={<SearchOutlined />} value={searchText} onChange={e => setSearchText(e.target.value)} allowClear style={{ maxWidth: 260, borderRadius: 3 }} size="small" />
                  <Button icon={selectionMode ? <CloseOutlined /> : <CheckSquareOutlined />}
                    onClick={() => { setSelectionMode(!selectionMode); setSelectedKeys(new Set()); }}
                    type={selectionMode ? 'primary' : 'default'} danger={selectionMode} size="small" style={S.plainBtn}>
                    {selectionMode ? 'Cancel' : 'Select'}
                  </Button>
                  {selectionMode && (
                    <>
                      <Button size="small" onClick={() => selectAllInFolder(files)} style={S.plainBtn}>
                        {files.every(f => selectedKeys.has(f.key)) ? 'Deselect All' : 'Select All'}
                      </Button>
                      <Popconfirm title={`Delete ${selectedKeys.size} selected files?`} onConfirm={handleBulkDelete} okText="Delete All" okButtonProps={{ danger: true, size: 'small' }}>
                        <Button type="primary" danger icon={<DeleteOutlined />} loading={deleting} disabled={selectedKeys.size === 0} size="small" style={S.plainBtn}>
                          Delete Selected ({selectedKeys.size})
                        </Button>
                      </Popconfirm>
                    </>
                  )}
                  <Button icon={<ReloadOutlined />} size="small" style={{ ...S.plainBtn, marginLeft: 'auto' }} onClick={() => fetchDocuments(selectedModule)} loading={loading}>Refresh</Button>
                </div>
                <Table
                  dataSource={files}
                  columns={fileColumns}
                  rowKey="key"
                  pagination={files.length > 20 ? { pageSize: 20, size: 'small' } : false}
                  size="small"
                  bordered
                  rowClassName={() => 'pc-row'}
                />
              </div>
            ),
          },
        ]} />
      </div>
    );
  };

  /* ═══════════════════════════════════════════
     RENDER: Main petty cash overview (folders list)
     ═══════════════════════════════════════════ */
  const renderPettyCashOverview = () => {
    return (
      <div>
        {/* Module filter */}
        <div style={{ marginBottom: 14 }}>
          <Space size={6}>
            {[{ key: 'all', label: 'All' }, { key: 'site-petty-cash', label: 'Site Petty Cash' }, { key: 'procurement-pc', label: 'Procurement PC' }].map(m => (
              <Button key={m.key} size="small"
                onClick={() => { setSelectedModule(m.key); setSelectedFolder(null); setSearchText(''); }}
                style={S.moduleBtn(selectedModule === m.key)}>{m.label}</Button>
            ))}
          </Space>
        </div>

        {/* Summary bar */}
        {documentsData && (
          <div style={S.summaryRow}>
            <div style={S.summaryCell(false)}>
              <div style={S.summaryLabel}>Total Documents</div>
              <div style={S.summaryValue()}>{summaryData.total_files}</div>
            </div>
            <div style={S.summaryCell(false)}>
              <div style={S.summaryLabel}>Site Petty Cash</div>
              <div style={S.summaryValue()}>{siteMod.total_files || 0}<span style={{ fontSize: 11, color: '#999', fontWeight: 400, marginLeft: 6 }}>{siteMod.folders_with_files || 0} folders</span></div>
            </div>
            <div style={S.summaryCell(true)}>
              <div style={S.summaryLabel}>Procurement PC</div>
              <div style={S.summaryValue()}>{procMod.total_files || 0}<span style={{ fontSize: 11, color: '#999', fontWeight: 400, marginLeft: 6 }}>{procMod.folders_with_files || 0} folders</span></div>
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div style={S.toolbar}>
          <Input placeholder="Search folders..." prefix={<SearchOutlined />} value={searchText} onChange={e => setSearchText(e.target.value)} allowClear style={{ maxWidth: 250, borderRadius: 3 }} size="small" />
          <Button icon={<ReloadOutlined />} size="small" style={S.plainBtn} onClick={() => fetchDocuments(selectedModule)} loading={loading}>Refresh</Button>
        </div>

        {/* Folders as bordered table — matches MIS PettyCashAdmin exactly */}
        <Table
          dataSource={foldersList
            .filter(f => !searchText.trim() || f.folder_number.includes(searchText.trim()))
            .map(f => ({ ...f, key: `${f.module || selectedModule}-${f.folder_number}` }))}
          columns={[
            {
              title: 'Folder', dataIndex: 'folder_number', key: 'fn', width: 100,
              render: (t, r) => r.file_count > 0
                ? <span onClick={() => { setSelectedFolder({ module: r.module || selectedModule, folderNum: r.folder_number }); setSearchText(''); }} style={S.link}>{t}</span>
                : <span style={S.cellMuted}>{t}</span>,
            },
            {
              title: 'Module', key: 'mod', width: 130,
              render: (_, r) => <span style={S.cellText}>{r.moduleLabel || '-'}</span>,
            },
            {
              title: 'Files', dataIndex: 'file_count', key: 'fc', width: 70, align: 'center',
              render: v => <span style={v > 0 ? S.cellBold : S.cellMuted}>{v}</span>,
            },
            {
              title: 'Size', dataIndex: 'total_size', key: 'sz', width: 100,
              render: v => <span style={S.cellMuted}>{formatFileSize(v)}</span>,
            },
          ]}
          pagination={foldersList.length > 30 ? { pageSize: 30, size: 'small' } : false}
          size="small"
          bordered
          rowClassName={() => 'pc-row'}
        />

        {foldersList.length === 0 && !loading && <Empty description="No documents uploaded yet" style={{ marginTop: 40 }} />}
      </div>
    );
  };

  /* ═══════════════════════════════════════════
     MAIN RENDER
     ═══════════════════════════════════════════ */
  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Preview Modal */}
      <Modal
        title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{getFileIcon(previewFilename, 14)}<span style={{ fontSize: 13, color: '#1a1a1a', fontWeight: 600, maxWidth: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{previewFilename}</span></div>}
        open={previewVisible}
        onCancel={() => { setPreviewVisible(false); setPreviewUrl(''); setPreviewFilename(''); }}
        footer={<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><Button icon={<DownloadOutlined />} onClick={() => { if (previewUrl) window.open(previewUrl, '_blank'); }}>Download</Button></div>}
        width={900} style={{ top: 30 }} styles={{ body: { padding: 0, height: '75vh' } }} destroyOnClose
      >
        {previewLoading ? <div style={{ height: '75vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin size="large" /></div> :
          previewUrl ? (() => {
            const ext = previewFilename?.split('.').pop().toLowerCase();
            if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext))
              return <div style={{ height: '75vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', overflow: 'auto' }}><img src={previewUrl} alt={previewFilename} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /></div>;
            return <iframe src={previewUrl} title={previewFilename} style={{ width: '100%', height: '75vh', border: 'none' }} />;
          })() : null
        }
      </Modal>

      {/* Sidebar */}
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} trigger={null} width={240} collapsedWidth={72}
        style={{ background: '#ffffff', borderRight: '1px solid #e8e8e8', position: 'fixed', left: 0, top: 0, bottom: 0, zIndex: 10, overflow: 'auto' }}>
        <div style={{ padding: collapsed ? '16px 0' : '16px 16px 12px', borderBottom: '1px solid #e8e8e8', textAlign: collapsed ? 'center' : 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: collapsed ? 'center' : 'flex-start' }}>
            <img src="/tab.png" alt="PTS" style={{ width: collapsed ? 32 : 38, height: collapsed ? 32 : 38, objectFit: 'contain', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
            {!collapsed && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a', letterSpacing: '-0.2px' }}>Phoenician Technical</div>
                <div style={{ fontSize: 10, color: '#888', letterSpacing: '0.4px', textTransform: 'uppercase' }}>MIS Platform</div>
              </div>
            )}
          </div>
        </div>
        <Menu mode="inline" selectedKeys={[selectedModule]} onClick={handleMenuClick} items={sidebarItems}
          style={{ border: 'none', marginTop: 4, padding: '0 4px', background: 'transparent', fontFamily: "'Segoe UI', Calibri, sans-serif", fontSize: 13 }} />
        {/* User info at bottom */}
        {!collapsed && (
          <div style={{ position: 'absolute', bottom: 12, left: 12, right: 12, padding: '10px 12px', background: '#f8f9fa', borderRadius: 4, border: '1px solid #e8e8e8' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar size={30} style={{ background: '#1a5276', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{initials}</Avatar>
              <div style={{ overflow: 'hidden', flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName}</div>
                <div style={{ fontSize: 10, color: '#888' }}>{role?.replace('_', ' ').toUpperCase() || 'ADMIN'}</div>
              </div>
            </div>
          </div>
        )}
      </Sider>

      <Layout style={{ marginLeft: collapsed ? 72 : 240, transition: 'margin-left 0.2s', background: '#f5f5f5' }}>
        {/* Header */}
        <Header style={{
          background: 'linear-gradient(135deg, #1a3c5e 0%, #1a5276 50%, #1e6391 100%)',
          padding: '0 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '2px solid #0f2a44',
          position: 'sticky', top: 0, zIndex: 9,
          height: 48, lineHeight: '48px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button type="text" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)} style={{ fontSize: 15, width: 32, height: 32, color: '#fff' }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: '#ffffff', fontFamily: "'Segoe UI', Calibri, sans-serif" }}>
              Phoenician Technical Services
            </span>
          </div>
          <Dropdown menu={{ items: profileMenuItems, onClick: handleProfileMenu }} trigger={['click']} placement="bottomRight">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 10px', borderRadius: 4 }}>
              <Avatar size={28} style={{ background: 'rgba(255,255,255,0.2)', fontSize: 11, fontWeight: 700 }}>{initials}</Avatar>
              <span style={{ fontSize: 12, color: '#fff', fontWeight: 500 }}>{displayName}</span>
            </div>
          </Dropdown>
        </Header>

        {/* Content */}
        <Content style={{ margin: 16, padding: 20, background: '#fff', borderRadius: 4, minHeight: 'calc(100vh - 48px - 50px - 32px)', border: '1px solid #f0f0f0' }}>
          <div style={S.page}>
            {isProcurementATX ? (
              <ProcurementAccountsView username={username} />
            ) : (
              <>
                {/* Page Header */}
                <div style={{ marginBottom: 14 }}>
                  <div style={S.pageTitle}>Petty Cash</div>
                  <div style={S.pageSubtitle}>Browse documents, view extracted data, manage folders</div>
                </div>

                {loading && !documentsData && <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>}
                {documentsData && (selectedFolder ? renderFolderDetail() : renderPettyCashOverview())}
              </>
            )}
          </div>
        </Content>

        <div style={{ textAlign: 'center', padding: '8px 24px', fontSize: 11, color: '#aaa', fontFamily: "'Segoe UI', Calibri, sans-serif" }}>
          Phoenician Technical Services LLC
        </div>
      </Layout>

      {/* Global style overrides */}
      <style>{`
        .pc-row td { border-bottom: 1px solid #e8e8e8 !important; }
        .pc-row:hover > td { background: transparent !important; }
        .ant-table-bordered .ant-table-thead > tr > th {
          background: #f5f5f5 !important;
          font-weight: 600 !important;
          font-size: 11.5px !important;
          color: #333 !important;
          border-bottom: 2px solid #d9d9d9 !important;
          padding: 6px 8px !important;
        }
        .ant-table-bordered .ant-table-tbody > tr > td {
          padding: 5px 8px !important;
          font-size: 12px !important;
        }
        .ant-table-bordered .ant-table-tbody > tr:hover > td { background: transparent !important; }
        .ant-table-cell-row-hover { background: transparent !important; }
        .ant-menu-light .ant-menu-item { border-radius: 0 !important; margin: 0 !important; }
        .ant-menu-light .ant-menu-item-selected { font-weight: 500; }
      `}</style>
    </Layout>
  );
};

export default AdminDashboard;